import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { searchHelp, type SearchResult } from "../../../lib/help/buildSearchIndex";

export function HelpSearch({ size = "md" }: { size?: "md" | "lg" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const results = useMemo<SearchResult[]>(() => searchHelp(query), [query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => setActiveIndex(0), [query]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      navigate(results[activeIndex]!.href);
      setOpen(false);
      setQuery("");
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const inputClass =
    size === "lg"
      ? "h-14 pl-14 pr-4 text-lg"
      : "h-11 pl-11 pr-4 text-base";
  const iconClass = size === "lg" ? "left-5 h-6 w-6" : "left-4 h-5 w-5";

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="relative block">
        <span className="sr-only">Search help</span>
        <MagnifyingGlassIcon
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-bark-600 ${iconClass}`}
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          placeholder="Search guides and FAQs…"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={`w-full rounded-full border border-cream-300 bg-cream-50 text-bark-900 shadow-sm placeholder:text-bark-600/70 focus:border-forest-500 focus:outline-none focus:ring-2 focus:ring-forest-300 ${inputClass}`}
        />
      </label>

      {open && query.trim().length >= 2 && (
        <div className="paper-card absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-cream-300 bg-cream-50 shadow-xl">
          {results.length === 0 ? (
            <p className="px-5 py-4 text-sm text-bark-700">
              No matches for "{query}". Try different keywords, or{" "}
              <a href="mailto:support@bookburrow.uk" className="text-forest-700 underline">
                email support
              </a>
              .
            </p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {results.map((r, i) => (
                <li key={r.href}>
                  <Link
                    to={r.href}
                    onClick={() => {
                      setOpen(false);
                      setQuery("");
                    }}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex flex-col gap-0.5 px-5 py-3 transition-colors ${
                      i === activeIndex ? "bg-forest-50" : "hover:bg-cream-100"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="rounded-full bg-forest-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-forest-700">
                        {r.kind === "guide" ? "Guide" : "FAQ"}
                      </span>
                      <span className="font-medium text-bark-900">{r.title}</span>
                    </span>
                    <span className="line-clamp-1 text-sm text-bark-700">{r.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
